/**
 * 个股 K 线实时推送 channel（`stock_kline`）。
 *
 * - 上行：`{ type: 'stock_kline', timestamp, param: { fullCode, timeIndex, candlePeriod, candleMode } }`
 * - 下行：与 `json_stock_realtime-kline-history` 一致（含 `quotations`）
 *
 * 模式说明：
 * - 使用 `realtimeHub.request()` 一问一答模式，每次请求携带 requestId
 * - 相同 exact param key（fullCode|timeIndex|candlePeriod|candleMode）的请求会复用进行中的 request
 * - 保留 3s 自动轮询；`timeIndex` 等 param 变更仅更新本地游标，不立即 flush
 * - 首次 install 时执行一次 flush，其后仅按轮询周期 request
 * - 下行按订阅维度键（fullCode|candlePeriod|candleMode）分发给对应消费者
 *
 * @module utils/realtime/stockChannels/stockKlineChannel
 */

import { unwrapKlineHistoryPayload } from '@/components/biz/bizStockCandlestickChart/hooks/stockKlineServices';
import { normalizeStockFullCode } from '@/utils/biz/stock';
import { normalizePushData } from '@/utils/realtime/normalizePushData';
import { WS_MESSAGE_TYPE } from '@/utils/realtime/wsMessageType';
import { realtimeHub } from '@/utils/realtime';

const KLINE_CHANNEL_ID = 'stock_kline';

/** 轮询间隔：3s */
const POLL_INTERVAL_MS = 3000;

/** 防抖间隔：0ms（立即执行） */
const DEBOUNCE_MS = 0;

/**
 * 归一化时段相关字段，用于 key 拼接；缺省值归一为 ''，保证与未传场景稳定区分。
 * @param {object} param
 * @returns {{ isRealTimeLine: string|number, tradeStatus: string|number }}
 */
function resolveSessionFields(param) {
  return {
    isRealTimeLine: param?.isRealTimeLine ?? '',
    tradeStatus: param?.tradeStatus ?? ''
  };
}

/**
 * 构建订阅维度键（不含 timeIndex；同 key 多 consumer 在 {@link _activeParams} 中取 min(timeIndex) 合并上行）。
 * 需纳入 isRealTimeLine/tradeStatus：分时与 1 分钟 K 的 fullCode|candlePeriod|candleMode 可能相同，
 * 美股盘前/盘中 session 不同也需独立订阅，否则 _activeParams 合并会串扰。
 * @param {{ fullCode: string, candlePeriod: number, candleMode: number, isRealTimeLine?: number, tradeStatus?: number }} param
 * @returns {string}
 */
function buildSubscriptionKey(param) {
  const { isRealTimeLine, tradeStatus } = resolveSessionFields(param);
  return `${normalizeStockFullCode(param.fullCode)}|${param.candlePeriod}|${
    param.candleMode
  }|${isRealTimeLine}|${tradeStatus}`;
}

/**
 * 构建精确参数键（含 timeIndex），用于 request 去重与 payload 分发。
 * @param {{ fullCode: string, timeIndex: string, candlePeriod: number, candleMode: number, isRealTimeLine?: number, tradeStatus?: number }} param
 * @returns {string}
 */
function buildExactParamKey(param) {
  const { isRealTimeLine, tradeStatus } = resolveSessionFields(param);
  return `${normalizeStockFullCode(param.fullCode)}|${param.timeIndex}|${param.candlePeriod}|${
    param.candleMode
  }|${isRealTimeLine}|${tradeStatus}`;
}

/**
 * 判断 timeIndex 是否相对上次"前进"。
 * - 优先按时间戳比较；
 * - 无法解析为时间时，回落到字符串字典序（兼容 YYYY-MM-DD HH:mm:ss）。
 * @param {string} previous
 * @param {string} next
 * @returns {boolean}
 */
function isTimeIndexAdvanced(previous, next) {
  if (!previous || !next || previous === next) {
    return false;
  }
  const prevTs = Date.parse(previous);
  const nextTs = Date.parse(next);
  if (Number.isFinite(prevTs) && Number.isFinite(nextTs)) {
    return nextTs > prevTs;
  }
  return next > previous;
}

/**
 * 同 subscriptionKey 下合并上行 param：取更早的 timeIndex，保证后挂载的消费者也能收到补推。
 * @param {{ fullCode: string, timeIndex: string, candlePeriod: number, candleMode: number }} a
 * @param {{ fullCode: string, timeIndex: string, candlePeriod: number, candleMode: number }} b
 * @returns {{ fullCode: string, timeIndex: string, candlePeriod: number, candleMode: number }}
 */
function pickEarlierSubscribeParam(a, b) {
  if (isTimeIndexAdvanced(a.timeIndex, b.timeIndex)) {
    return a;
  }
  if (isTimeIndexAdvanced(b.timeIndex, a.timeIndex)) {
    return b;
  }
  return a.timeIndex <= b.timeIndex ? a : b;
}

/**
 * 构建上行 payload。
 * @param {{ fullCode: string, timeIndex: string, candlePeriod: number, candleMode: number }} param
 * @returns {object|null}
 */
function buildRequestPayload(param) {
  if (!param) {
    return null;
  }
  return {
    type: WS_MESSAGE_TYPE.stock.STOCK_KLINE,
    timestamp: Date.now(),
    param: param
  };
}

/**
 * 解析下行 data 段为 klineHistory 结构。
 * @param {unknown} data
 * @returns {object|null}
 */
function parseStockKlinePush(data) {
  return unwrapKlineHistoryPayload(normalizePushData(data));
}

/**
 * K 线实时订阅器：按 consumerId 维护订阅 param，使用 request 模式一问一答。
 */
export class StockKlineChannelSubscriber {
  static id = KLINE_CHANNEL_ID;

  constructor() {
    /**
     * 消费者映射：consumerId -> normalizedParam
     * @type {Map<string|symbol, object>}
     */
    this._consumers = new Map();

    /**
     * 监听器映射：listener -> consumerId
     * 用于 payload 返回时按 consumer 的当前 param 精准分发
     * @type {Map<Function, string|symbol>}
     */
    this._pushListeners = new Map();

    /**
     * 进行中的请求映射：exactParamKey -> Promise
     * 用于相同参数的请求复用
     * @type {Map<string, Promise>}
     */
    this._pendingRequests = new Map();

    /**
     * 防抖/轮询定时器
     * @type {ReturnType<typeof setTimeout> | null}
     */
    this._requestTimer = null;

    /**
     * 是否已启动
     * @type {boolean}
     */
    this._started = false;
  }

  /**
   * 注册下行推送监听（多消费者各自注册，卸载时需 {@link removePushListener}）。
   * @param {string|symbol} consumerId 消费者唯一标识
   * @param {(payload: object) => void} listener 监听器回调
   * @returns {void}
   */
  addPushListener(consumerId, listener) {
    if (typeof listener !== 'function') {
      return;
    }
    // 记录该监听器属于哪个 consumer，用于精准分发
    this._pushListeners.set(listener, consumerId);
  }

  /**
   * 移除下行推送监听。
   * @param {(payload: object) => void} listener 监听器回调
   * @returns {void}
   */
  removePushListener(listener) {
    this._pushListeners.delete(listener);
  }

  /**
   * 按订阅维度键分发 payload（与请求时的 timeIndex 解耦，避免游标已前进导致丢包）。
   * @param {{ fullCode: string, candlePeriod: number, candleMode: number }} requestParam 发起 request 时的 param
   * @param {object} payload 解析后的 payload
   * @returns {void}
   */
  _emitPush(requestParam, payload) {
    if (!requestParam) {
      return;
    }
    const subKey = buildSubscriptionKey(requestParam);
    for (const [listener, consumerId] of this._pushListeners.entries()) {
      const consumerParam = this._consumers.get(consumerId);
      if (!consumerParam) {
        continue;
      }
      if (buildSubscriptionKey(consumerParam) !== subKey) {
        continue;
      }
      try {
        listener(payload);
      } catch (err) {
        console.error('[StockKlineChannel] listener threw:', err);
      }
    }
  }

  /**
   * 汇总各 consumer 的上行 param；同 subscriptionKey 取 min(timeIndex) 合并上行。
   * @returns {object[]}
   */
  _activeParams() {
    const map = new Map();
    for (const param of this._consumers.values()) {
      if (!param) {
        continue;
      }
      const key = buildSubscriptionKey(param);
      const existing = map.get(key);
      map.set(key, existing ? pickEarlierSubscribeParam(existing, param) : param);
    }
    return [...map.values()];
  }

  /**
   * 启动 channel（首次订阅时调用）。
   * 建立连接并立即执行首次 flush。
   * @returns {void}
   */
  _start() {
    if (this._started) {
      return;
    }
    this._started = true;
    realtimeHub.connect();
    this._scheduleFlush();
  }

  /**
   * 停止 channel（无消费者时调用）。
   * 清理定时器。
   * @returns {void}
   */
  _stop() {
    if (!this._started) {
      return;
    }
    this._started = false;
    this._cancelTimer();
    // 注意：不主动取消进行中的 request，返回时通过 consumer/key 检查自然丢弃
  }

  /**
   * 取消当前定时器。
   * @returns {void}
   */
  _cancelTimer() {
    if (this._requestTimer) {
      clearTimeout(this._requestTimer);
      this._requestTimer = null;
    }
  }

  /**
   * 调度 flush：防抖后立即执行，然后进入轮询循环。
   * @returns {void}
   */
  _scheduleFlush() {
    this._cancelTimer();
    this._requestTimer = setTimeout(() => {
      this._requestTimer = null;
      this._flush();
      // 进入下一轮轮询
      this._schedulePoll();
    }, DEBOUNCE_MS);
  }

  /**
   * 调度轮询：3s 后执行。
   * @returns {void}
   */
  _schedulePoll() {
    if (!this._started) {
      return;
    }
    this._requestTimer = setTimeout(() => {
      this._requestTimer = null;
      if (!this._started) {
        return;
      }
      this._flush();
      this._schedulePoll();
    }, POLL_INTERVAL_MS);
  }

  /**
   * 执行请求：为每个唯一 exact param 发起 request，复用进行中的请求。
   * @returns {void}
   */
  _flush() {
    const params = this._activeParams();
    if (!params.length) {
      return;
    }

    // 为每个 exact param 发起 request
    for (const param of params) {
      const exactKey = buildExactParamKey(param);

      // 检查是否已有进行中的请求
      if (this._pendingRequests.has(exactKey)) {
        // 复用进行中的请求，不重复发送
        continue;
      }

      const payload = buildRequestPayload(param);
      if (!payload) {
        continue;
      }

      // 发起 request 并缓存 promise
      const requestPromise = realtimeHub
        .request(payload)
        .then((data) => {
          // 解析并分发
          const parsedPayload = parseStockKlinePush(data);
          if (parsedPayload) {
            this._emitPush(param, parsedPayload);
          }
        })
        .catch((err) => {
          // 请求失败（超时、断连等），只打印错误，不中断轮询
          console.warn('[StockKlineChannel] request failed:', err?.message || err);
        })
        .finally(() => {
          // 清理 pending，下一轮轮询可以重新请求
          this._pendingRequests.delete(exactKey);
        });

      this._pendingRequests.set(exactKey, requestPromise);
    }
  }

  /**
   * 订阅：consumerId 订阅指定 param。
   * @param {string|symbol} consumerId 消费者唯一标识
   * @param {object|null|undefined} param 订阅参数
   * @returns {void}
   */
  subscribe(consumerId, param) {
    if (!consumerId || !param) {
      return;
    }

    const previous = this._consumers.get(consumerId);
    const previousKey = previous ? buildSubscriptionKey(previous) : null;
    const nextKey = buildSubscriptionKey(param);
    if (previous && previousKey === nextKey && previous.timeIndex === param.timeIndex) {
      return;
    }

    this._consumers.set(consumerId, param);

    if (!this._started) {
      this._start();
      return;
    }
    // 日 / 周 / 月 K 切换等导致订阅维度变更时立即拉取，避免等下一轮 3s 轮询
    if (previousKey !== nextKey) {
      this._scheduleFlush();
    }
  }

  /**
   * 取消订阅：consumerId 取消订阅。
   * @param {string|symbol} consumerId 消费者唯一标识
   * @returns {void}
   */
  unsubscribe(consumerId) {
    if (!consumerId || !this._consumers.has(consumerId)) {
      return;
    }
    this._consumers.delete(consumerId);
    if (!this._consumers.size) {
      this._stop();
    }
  }

  /**
   * 销毁 channel。
   * 清理所有资源。
   * @returns {void}
   */
  destroy() {
    this._stop();
    this._consumers.clear();
    this._pushListeners.clear();
    this._pendingRequests.clear();
  }
}

/** @type {StockKlineChannelSubscriber | null} */
let stockKlineChannelSubscriberSingleton = null;

/**
 * 获取 `stock_kline` 通道共享订阅器（单例）。
 * @returns {StockKlineChannelSubscriber}
 */
export function getStockKlineChannelSubscriber() {
  if (!stockKlineChannelSubscriberSingleton) {
    stockKlineChannelSubscriberSingleton = new StockKlineChannelSubscriber();
  }
  return stockKlineChannelSubscriberSingleton;
}
